import { base64, hex } from "@scure/base";
import { Script, Transaction } from "@scure/btc-signer";
import { addListener, AddressPurpose, BitcoinNetworkType, MessageSigningProtocols, request, signMultipleTransactions, type BitcoinProvider } from "sats-connect";

import type { ConnectedWallet, MempoolUtxo, WalletAddress, WalletProviderId } from "./types";
import { createWalletOwnershipChallenge, verifyBip322Signature } from "./wallet-proof";
import { finalizeRefundPsbt, type RefundQuote } from "./recovery-refund";
import { localWalletAdapter } from "./stc-wallet/adapter";

const PROVIDER_PATHS: Record<WalletProviderId, string> = {
  xverse: "XverseProviders.BitcoinProvider",
  unisat: "unisat",
  stc: "",
};

interface UnisatLike {
  on?: (event: "accountsChanged" | "networkChanged", listener: () => void) => void;
  removeListener?: (event: "accountsChanged" | "networkChanged", listener: () => void) => void;
  requestAccounts?: () => Promise<string[]>;
  getAccounts?: () => Promise<string[]>;
  getPublicKey?: () => Promise<string>;
  getNetwork?: () => Promise<string>;
  signMessage?: (message: string, type?: "bip322-simple" | "ecdsa") => Promise<string>;
  signPsbt?: (psbt: string, options: {
    autoFinalized: boolean;
    toSignInputs: Array<{ index: number; address: string; sighashTypes?: number[]; useTweakedSigner?: boolean }>;
  }) => Promise<string>;
  getBitcoinUtxos?: () => Promise<Array<{ txid: string; vout: number; satoshis: number }>>;
  signPsbts?: (psbts: string[], options: Array<{
    autoFinalized: boolean;
    toSignInputs: Array<{ index: number; address: string; sighashTypes?: number[]; useTweakedSigner?: boolean }>;
  }>) => Promise<string[]>;
}

interface WalletWindow {
  XverseProviders?: { BitcoinProvider?: unknown };
  BitcoinProvider?: unknown;
  unisat?: UnisatLike;
  unisat_wallet?: UnisatLike;
}

export type WalletConnectionCandidate = Omit<ConnectedWallet, "ownershipVerified"> & {
  ownershipVerified: false;
};

const walletWindow = (): WalletWindow => globalThis as typeof globalThis & WalletWindow;

function unisatProvider(): UnisatLike | undefined {
  const candidate = walletWindow();
  const officialProvider = candidate.unisat_wallet;
  if (officialProvider?.requestAccounts && officialProvider.getPublicKey) return officialProvider;
  return candidate.unisat;
}

function normalizeAddress(
  address: {
    address: string;
    publicKey: string;
    purpose: string;
    addressType: string;
  },
): WalletAddress {
  return {
    address: address.address,
    publicKey: address.publicKey,
    purpose: address.purpose === "payment" ? "payment" : "ordinals",
    addressType: address.addressType,
  };
}

function providerInstalled(provider: WalletProviderId): boolean {
  if (provider === "stc") return false;
  const candidate = walletWindow();
  if (provider === "xverse") return Boolean(candidate.XverseProviders?.BitcoinProvider ?? candidate.BitcoinProvider);
  return Boolean(unisatProvider());
}

export function walletAvailability(): Record<WalletProviderId, boolean> {
  return {
    xverse: providerInstalled("xverse"),
    unisat: providerInstalled("unisat"),
    stc: false,
  };
}

function providerPath(provider: WalletProviderId): string {
  const candidate = walletWindow();
  if (provider === "xverse" && !candidate.XverseProviders?.BitcoinProvider && candidate.BitcoinProvider) return "BitcoinProvider";
  return PROVIDER_PATHS[provider];
}

export function subscribeWalletChanges(provider: WalletProviderId, listener: () => void): () => void {
  if (!providerInstalled(provider)) return () => {};
  if (provider === "unisat") {
    const unisat = unisatProvider();
    unisat?.on?.("accountsChanged", listener);
    unisat?.on?.("networkChanged", listener);
    return () => {
      unisat?.removeListener?.("accountsChanged", listener);
      unisat?.removeListener?.("networkChanged", listener);
    };
  }
  const remove: Array<() => void> = [];
  for (const eventName of ["accountChange", "networkChange", "disconnect"] as const) {
    // Older extensions may not implement listeners. Fresh proofs still read the selected account.
    try { remove.push(addListener({ eventName, cb: listener }, providerPath(provider))); } catch { /* Optional provider capability. */ }
  }
  return () => remove.forEach(unsubscribe => unsubscribe());
}

export async function assertWalletSelected(wallet: Pick<ConnectedWallet, "provider" | "payment" | "ordinals">): Promise<void> {
  if (wallet.provider === "stc") { localWalletAdapter(wallet); return; }
  if (wallet.provider === "unisat") {
    const provider = unisatProvider();
    const addresses = await provider?.getAccounts?.();
    const network = await provider?.getNetwork?.();
    const publicKey = await provider?.getPublicKey?.();
    if (!addresses?.includes(wallet.payment.address)
      || wallet.payment.address !== wallet.ordinals.address
      || publicKey?.toLowerCase() !== wallet.payment.publicKey.toLowerCase()
      || publicKey?.toLowerCase() !== wallet.ordinals.publicKey.toLowerCase()
      || (network && network !== "livenet" && network !== "mainnet")) {
      throw new Error("Wallet changed. Select the intended UniSat account on mainnet and try again");
    }
    return;
  }
  const selected = await request("wallet_getAccount", null, providerPath(wallet.provider));
  if (selected.status !== "success" || selected.result.network.bitcoin.name !== BitcoinNetworkType.Mainnet
    || !selected.result.addresses.some(item => item.purpose === AddressPurpose.Payment && item.address === wallet.payment.address && item.publicKey === wallet.payment.publicKey)
    || !selected.result.addresses.some(item => item.purpose === AddressPurpose.Ordinals && item.address === wallet.ordinals.address && item.publicKey === wallet.ordinals.publicKey)) {
    throw new Error("Wallet changed. Select the intended Xverse account on mainnet and try again");
  }
}

export async function signWalletOwnershipMessage(
  wallet: Pick<ConnectedWallet, "provider">,
  address: string,
  message: string,
): Promise<string> {
  if (wallet.provider === "stc") return localWalletAdapter(wallet).signMessage(address, message);
  if (wallet.provider === "unisat") {
    const unisat = unisatProvider();
    if (!unisat?.signMessage) throw new Error("This version of UniSat does not support message signing");
    const signature = await unisat.signMessage(message, "bip322-simple");
    if (!verifyBip322Signature(address, message, signature)) {
      throw new Error("Wallet ownership could not be verified");
    }
    return signature;
  }

  const proof = await request(
    "signMessage",
    {
      address,
      message,
      protocol: MessageSigningProtocols.BIP322,
    },
    providerPath(wallet.provider),
  );
  if (proof.status !== "success") throw new Error(proof.error.message || "Wallet verification was cancelled");
  if (proof.result.address.toLowerCase() !== address.toLowerCase() || proof.result.protocol !== MessageSigningProtocols.BIP322) {
    throw new Error("This wallet could not provide a BIP-322 ownership proof");
  }
  if (!verifyBip322Signature(address, message, proof.result.signature)) {
    throw new Error("Wallet ownership could not be verified");
  }
  return proof.result.signature;
}

export async function connectWalletAddresses(provider: WalletProviderId, appName: string): Promise<WalletConnectionCandidate> {
  if (provider !== "xverse" && provider !== "unisat") throw new Error("Only Xverse and UniSat are supported");
  if (!providerInstalled(provider)) throw new Error(`${provider === "xverse" ? "Xverse" : "UniSat"} wallet is not installed`);

  if (provider === "unisat") {
    const unisat = unisatProvider();
    if (!unisat?.requestAccounts || !unisat.getPublicKey) {
      throw new Error("Update UniSat Wallet before connecting");
    }
    const network = await unisat.getNetwork?.();
    if (network && network !== "livenet" && network !== "mainnet") {
      throw new Error("Switch UniSat to Bitcoin mainnet before connecting");
    }
    const accounts = await unisat.requestAccounts();
    const address = accounts[0];
    if (!address) throw new Error("UniSat did not provide a Bitcoin address");
    if (!address.startsWith("bc1p")) {
      throw new Error("Switch UniSat's address type to Taproot (bc1p…), then connect again");
    }
    const publicKey = await unisat.getPublicKey();
    if (!publicKey) throw new Error("UniSat did not provide the public key for this address");
    const walletAddress = { address, publicKey, addressType: "p2tr" };
    return {
      provider,
      payment: { ...walletAddress, purpose: "payment" },
      ordinals: { ...walletAddress, purpose: "ordinals" },
      ownershipVerified: false,
    };
  }

  const selected = await request("wallet_getAccount", null, providerPath(provider));
  const response = selected.status === "success" ? selected : await request(
    "wallet_connect",
    {
      addresses: [AddressPurpose.Payment, AddressPurpose.Ordinals],
      message: `${appName} uses your wallet only to build and sign inscriptions`,
      network: BitcoinNetworkType.Mainnet,
    },
    providerPath(provider),
  );
  if (response.status !== "success") throw new Error(response.error.message || "Wallet connection was cancelled");
  if (response.result.network.bitcoin.name !== BitcoinNetworkType.Mainnet) throw new Error("Switch Xverse to Bitcoin mainnet before connecting");

  const addresses = response.result.addresses.map(normalizeAddress);
  const payment = addresses.find((address) => address.purpose === "payment");
  const ordinals = addresses.find((address) => address.purpose === "ordinals");
  if (!payment || !ordinals) throw new Error("The wallet did not provide the required Bitcoin addresses");
  if (!ordinals.address.startsWith("bc1p")) throw new Error("Your ordinals address must be a Taproot address (bc1p…)");

  return { provider, payment, ordinals, ownershipVerified: false };
}

export async function connectWallet(provider: WalletProviderId, appName: string): Promise<ConnectedWallet> {
  const candidate = await connectWalletAddresses(provider, appName);

  const challenge = createWalletOwnershipChallenge(appName, candidate.payment.address);
  await signWalletOwnershipMessage(candidate, candidate.payment.address, challenge);

  return { ...candidate, ownershipVerified: true };
}

export async function signPsbt(
  wallet: ConnectedWallet,
  psbt: Uint8Array,
  address: string,
  inputIndexes: number[],
): Promise<Uint8Array> {
  return signPsbtGroups(wallet, psbt, { [address]: inputIndexes });
}

export async function signPsbtGroups(
  wallet: ConnectedWallet,
  psbt: Uint8Array,
  signInputs: Record<string, number[]>,
): Promise<Uint8Array> {
  if (wallet.provider === "stc") {
    const groups = Object.entries(signInputs).map(([address, inputIndexes]) => ({ address, inputIndexes, useTweakedSigner: address.startsWith("bc1p") }));
    if (!groups.length) throw new Error("No inputs selected for signing");
    const result = await localWalletAdapter(wallet).signTransactions([{ key: "transaction", psbtBase64: base64.encode(psbt), ...groups[0]!, signingGroups: groups }], "STC transaction");
    if (!result.transaction) throw new Error("STC Wallet returned no signature");
    return base64.decode(result.transaction);
  }
  if (wallet.provider === "unisat") {
    const unisat = unisatProvider();
    if (!unisat?.signPsbt) throw new Error("This version of UniSat does not support transaction signing");
    const transaction = Transaction.fromPSBT(psbt, { allowUnknownInputs: true, allowUnknownOutputs: true, disableScriptCheck: true });
    const signed = await unisat.signPsbt(hex.encode(psbt), {
      autoFinalized: false,
      toSignInputs: Object.entries(signInputs).flatMap(([address, indexes]) => indexes.map((index) => {
        const walletAddress = [wallet.ordinals, wallet.payment].find((candidate) => candidate.address === address);
        const signingKey = walletAddress ? normalizeXOnlyPublicKey(walletAddress.publicKey) : undefined;
        const originalKeyInLeaf = signingKey && transaction.getInput(index).tapLeafScript?.some(([, leaf]) => (
          Script.decode(leaf.slice(0, -1)).some((item) => item instanceof Uint8Array && hex.encode(item) === hex.encode(signingKey))
        ));
        return {
          index,
          address,
          // Parents use the tweaked key path; inscription leaves contain the
          // original key. Forcing a tweak for both produces an unusable reveal.
          useTweakedSigner: address.startsWith("bc1p") && !originalKeyInLeaf,
        };
      })),
    });
    return hex.decode(signed);
  }

  const response = await request(
    "signPsbt",
    {
      psbt: base64.encode(psbt),
      signInputs,
      broadcast: false,
    },
    providerPath(wallet.provider),
  );
  if (response.status !== "success") throw new Error(response.error.message || "Signature request was cancelled");
  return base64.decode(response.result.psbt);
}

export async function assertRecoveryRefundWallet(wallet: ConnectedWallet): Promise<void> {
  if (wallet.provider !== "xverse") throw new Error("UniSat’s connected signing API has not been verified for this commit’s Merkle-root tweak. Refund signing is unavailable; your saved package is preserved.");
  await assertWalletSelected(wallet);
  const account = await request("wallet_getAccount", null, providerPath(wallet.provider));
  if (account.status !== "success" || account.result.walletType !== "software") throw new Error("This refund currently supports Xverse software accounts only. Hardware-wallet signing of this Taproot tree has not been verified.");
}

export async function signRecoveryRefund(wallet: ConnectedWallet, quote: RefundQuote): Promise<string> {
  await assertRecoveryRefundWallet(wallet);
  let signed: Uint8Array;
  try { signed = await signPsbt(wallet, base64.decode(quote.psbt), wallet.ordinals.address, [0]); }
  catch { throw new Error("Refund signing was cancelled or this Xverse version could not sign the commit’s Taproot key path. No refund was sent; the saved package is preserved."); }
  await assertWalletSelected(wallet);
  return finalizeRefundPsbt(signed, quote, { paymentAddress: wallet.payment.address, ordinalsAddress: wallet.ordinals.address, ordinalsPublicKey: wallet.ordinals.publicKey });
}

export interface WalletPsbtRequest {
  key: string;
  psbtBase64: string;
  address: string;
  inputIndexes: number[];
  sighash?: number;
  useTweakedSigner: boolean;
  signingGroups?: Array<{
    address: string;
    inputIndexes: number[];
    sighash?: number;
    useTweakedSigner: boolean;
  }>;
}

export function unisatSigningInputs(item: WalletPsbtRequest): Array<{ index: number; address: string; sighashTypes?: number[]; useTweakedSigner: boolean }> {
  return (item.signingGroups ?? [{ address: item.address, inputIndexes: item.inputIndexes, sighash: item.sighash, useTweakedSigner: item.useTweakedSigner }])
    .flatMap((group) => group.inputIndexes.map((index) => ({
      index,
      address: group.address,
      ...(group.sighash === undefined ? {} : { sighashTypes: [group.sighash] }),
      useTweakedSigner: group.useTweakedSigner,
    })));
}

export function xverseSigningInputs(item: WalletPsbtRequest): Array<{ address: string; signingIndexes: number[]; sigHash?: number }> {
  return (item.signingGroups ?? [{ address: item.address, inputIndexes: item.inputIndexes, sighash: item.sighash }]).map((group) => ({
    address: group.address,
    signingIndexes: group.inputIndexes,
    ...(group.sighash === undefined ? {} : { sigHash: group.sighash }),
  }));
}

export async function signMultiplePsbts(wallet: ConnectedWallet, requests: WalletPsbtRequest[], message: string): Promise<Record<string, string>> {
  if (!requests.length) return {};
  if (wallet.provider === "stc") return localWalletAdapter(wallet).signTransactions(requests, message);
  const maximum = wallet.provider === "xverse" ? 25 : 100;
  if (requests.length > maximum) throw new Error(`${wallet.provider === "xverse" ? "Xverse" : "UniSat"} can sign at most ${maximum} transactions at once`);
  if (wallet.provider === "unisat") {
    const unisat = unisatProvider();
    if (!unisat?.signPsbts) throw new Error("This version of UniSat does not support batch transaction signing");
    const signedHex = await unisat.signPsbts(
      requests.map((item) => hex.encode(base64.decode(item.psbtBase64))),
      requests.map((item) => ({
        autoFinalized: false,
        toSignInputs: unisatSigningInputs(item),
      })),
    );
    if (signedHex.length !== requests.length) throw new Error("UniSat returned an incomplete signing batch");
    return Object.fromEntries(requests.map((item, index) => [item.key, base64.encode(hex.decode(signedHex[index] ?? ""))]));
  }

  const provider = walletWindow().XverseProviders?.BitcoinProvider ?? walletWindow().BitcoinProvider;
  if (!provider) throw new Error("Xverse wallet is not installed");
  const responses = await new Promise<Array<{ psbtBase64: string }>>((resolve, reject) => {
    void signMultipleTransactions({
      payload: {
        network: { type: BitcoinNetworkType.Mainnet },
        message,
        psbts: requests.map((item) => ({
          psbtBase64: item.psbtBase64,
          inputsToSign: xverseSigningInputs(item),
        })),
      },
      getProvider: () => Promise.resolve(provider as BitcoinProvider),
      onFinish: resolve,
      onCancel: () => reject(new Error("Transaction signing was cancelled")),
    });
  });
  if (responses.length !== requests.length) throw new Error("Xverse returned an incomplete signing batch");
  return Object.fromEntries(requests.map((item, index) => [item.key, responses[index]?.psbtBase64 ?? ""]));
}

export async function keepCardinalUtxos(
  wallet: ConnectedWallet,
  mempoolUtxos: MempoolUtxo[],
): Promise<MempoolUtxo[]> {
  const activeUnisat = unisatProvider();
  if (wallet.provider !== "unisat" || !activeUnisat?.getBitcoinUtxos) return mempoolUtxos;
  const walletUtxos = await activeUnisat.getBitcoinUtxos();
  const cardinalOutpoints = new Set(walletUtxos.map((utxo) => `${utxo.txid}:${utxo.vout}`));
  return mempoolUtxos.filter((utxo) => cardinalOutpoints.has(`${utxo.txid}:${utxo.vout}`));
}

export function shortenAddress(address: string): string {
  return `${address.slice(0, 6)}…${address.slice(-6)}`;
}

export function normalizeXOnlyPublicKey(publicKey: string): Uint8Array {
  const bytes = hex.decode(publicKey);
  if (bytes.length === 32) return bytes;
  if (bytes.length === 33) return bytes.slice(1);
  throw new Error("Wallet returned an unsupported Taproot public key");
}
